import type mapboxgl from 'mapbox-gl';
import { useCallback, useRef, useState } from 'react';

import { useDebounce } from './useDebounce';

const TOOLTIP_HIDE_DELAY_MS = 120;

type HoverResult = {
  feature: TooltipFeature;
  coords: [number, number] | null;
  changed: boolean;
};

type TooltipFeature = {
  id: number;
  type: number;
  label?: unknown;
  location?: unknown;
} | null;

function featureKey(f: TooltipFeature): string | null {
  if (!f) return null;
  return `${f.id}:${f.type}`;
}

export function useHoveredFeature(
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  layerIds: string[],
  options?: {
    buildFeature: (
      props: Record<string, unknown>,
      layerId: string
    ) => TooltipFeature | Record<string, unknown> | null;
    throttleMs?: number;
  }
): {
  hover: HoverResult;
  // biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
  attach: (map: mapboxgl.Map) => void | (() => void);
  detach: () => void;
} {
  const { buildFeature, throttleMs = 50 } = options || {};
  const lastKeyRef = useRef<string | null>(null);
  const pendingLeaveRef = useRef<number | null>(null);
  const [hover, setHover] = useState<HoverResult>({
    feature: null,
    coords: null,
    changed: false,
  });

  const clearLeaveTimeout = useCallback(() => {
    if (pendingLeaveRef.current != null) {
      window.clearTimeout(pendingLeaveRef.current);
      pendingLeaveRef.current = null;
    }
  }, []);

  const { run: setHoverThrottled } = useDebounce((next: HoverResult) => {
    setHover((prev) => {
      const prevKey = featureKey(prev.feature);
      const nextKey = featureKey(next.feature);
      const changed = prevKey !== nextKey;
      if (changed) return { ...next, changed: true };
      return { ...next, changed: false };
    });
  }, throttleMs);

  const attach = useCallback(
    (map: mapboxgl.Map) => {
      mapRef.current = map;

      const handleMove = (
        e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }
      ) => {
        if (!mapRef.current) return;
        const features = mapRef.current.queryRenderedFeatures(e.point, {
          layers: layerIds,
        });
        const f = features?.[0];
        if (!f) {
          if (!pendingLeaveRef.current) {
            pendingLeaveRef.current = window.setTimeout(() => {
              const canvas = mapRef.current?.getCanvas();
              if (canvas) canvas.style.cursor = '';
              lastKeyRef.current = null;
              setHover({ feature: null, coords: null, changed: true });
              pendingLeaveRef.current = null;
            }, TOOLTIP_HIDE_DELAY_MS);
          }
          return;
        }

        clearLeaveTimeout();
        const rawBuilt = buildFeature?.(f.properties || {}, f.layer?.id ?? '');
        const enriched = (() => {
          if (!rawBuilt) return null;
          const rb = rawBuilt as {
            id?: unknown;
            type?: unknown;
            label?: unknown;
            location?: unknown;
          };
          if (typeof rb.id !== 'undefined' && typeof rb.type !== 'undefined') {
            const idNum = Number(rb.id as number | string);
            const typeNum = Number(rb.type as number | string);
            if (!Number.isNaN(idNum) && !Number.isNaN(typeNum)) {
              return {
                id: idNum,
                type: typeNum,
                label: rb.label,
                location: rb.location,
              } as TooltipFeature;
            }
          }
          return null;
        })();
        if (!enriched) {
          const canvas = mapRef.current.getCanvas();
          canvas.style.cursor = '';
          lastKeyRef.current = null;
          setHover({ feature: null, coords: null, changed: true });
          return;
        }

        const key = featureKey(enriched);
        const coords = (() => {
          const g = f.geometry;
          if (g?.type === 'Point') {
            const c = (g as unknown as { coordinates?: unknown })
              .coordinates as unknown;
            if (
              Array.isArray(c) &&
              typeof c[0] === 'number' &&
              typeof c[1] === 'number'
            ) {
              return [c[0], c[1]] as [number, number];
            }
          }
          return [e.lngLat.lng, e.lngLat.lat] as [number, number];
        })();

        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = 'pointer';
        }
        setHoverThrottled({
          feature: enriched,
          coords,
          changed: lastKeyRef.current !== key,
        });
        lastKeyRef.current = key;
      };

      const handleLeave = () => {
        if (!pendingLeaveRef.current) {
          pendingLeaveRef.current = window.setTimeout(() => {
            const canvas = mapRef.current?.getCanvas();
            if (canvas) canvas.style.cursor = '';
            lastKeyRef.current = null;
            setHover({ feature: null, coords: null, changed: true });
            pendingLeaveRef.current = null;
          }, TOOLTIP_HIDE_DELAY_MS);
        }
      };

      map.on('mousemove', handleMove);
      const canvas = map.getCanvas();
      canvas.addEventListener('mouseleave', handleLeave);

      return () => {
        clearLeaveTimeout();
        map.off('mousemove', handleMove);
        canvas.removeEventListener('mouseleave', handleLeave);
      };
    },
    [buildFeature, layerIds, mapRef, setHoverThrottled, clearLeaveTimeout]
  );

  const detach = useCallback(() => {
    clearLeaveTimeout();
    lastKeyRef.current = null;
    setHover({ feature: null, coords: null, changed: true });
  }, [clearLeaveTimeout]);

  return { hover, attach, detach } as const;
}
